import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "./InputField";
import { AnimatePresence, motion } from "framer-motion";
import { useNotification } from "./NotificationContext";
import { db, storage } from "@lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { Country, State, City } from "country-state-city";

export const InputFieldRace = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
  eventId,
  categoryName,
  eventDate,
  updateUserData,
}) => {
  const { showNotification } = useNotification();
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [countryMap, setCountryMap] = useState({});
  const [stateMap, setStateMap] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);

  // === NEW: Parent Consent for under-18 participants in 10K ===
  const [parentConsentUploading, setParentConsentUploading] = useState(false);
  const [parentConsentError, setParentConsentError] = useState("");

  // country-state-city package - offline, no API calls needed

  // === HELPER: Check if category is 10K ===
  const is10KCategory = useMemo(() => {
    if (!categoryName) return false;
    const normalized = categoryName.toString().toLowerCase();
    return normalized.includes("10k") || normalized.includes("10 k") || normalized.includes("kids");
  }, [categoryName]);

  const isSMM7KCategory = useMemo(() => {
    if (eventId !== "SMM" || !categoryName) return false;
    const normalized = categoryName.toString().toLowerCase();
    return (
      normalized.includes("7k") ||
      normalized.includes("7 km") ||
      normalized.includes("7km")
    );
  }, [eventId, categoryName]);

  // === HELPER: Calculate age from date of birth ===
  const calculateAge = (dob, refDate) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return null;
    const reference = refDate || new Date();
    let age = reference.getFullYear() - birthDate.getFullYear();
    const monthDiff = reference.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && reference.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  // === COMPUTED: Is participant under 18? ===
  const participantAge = useMemo(
    () => calculateAge(formData.tanggalLahir, eventDate),
    [formData.tanggalLahir, eventDate]
  );
  
  // For JMM, we strictly enforce 18+ regardless of category
  // For other events, we allow under-18 in 10K category with consent
  const isUnder18 = useMemo(() => {
    if (participantAge === null) return false;
    if (eventId === "JMM") return participantAge < 18;
    return participantAge < 18;
  }, [participantAge, eventId]);

  // Show consent upload for under-18 participants in youth categories, but only
  // when the participant actually clears the category's own age floor. SMM 7 KM
  // requires 13+, so offering the upload to a 7-year-old promised a registration
  // the server would always reject (see validateTanggalLahir in
  // server/routes/payment.js).
  const meetsCategoryMinimumAge = useMemo(() => {
    if (participantAge === null) return true;
    if (isSMM7KCategory) return participantAge >= 13;
    return true;
  }, [participantAge, isSMM7KCategory]);

  const showParentConsent = useMemo(() => {
    if (eventId === "JMM") return false; // Strictly 18+ for JMM, no consent option
    if (!meetsCategoryMinimumAge) return false;
    return isUnder18 && (is10KCategory || isSMM7KCategory);
  }, [eventId, isUnder18, is10KCategory, isSMM7KCategory, meetsCategoryMinimumAge]);

  // === PARENT CONSENT FORM DOWNLOAD URL ===
  const getParentConsentForm = (eventId, isForeigner) => {
    const formMap = {
      BMM: { label: "Download Consent Form Template", url: "/forms/parent-consent-bmm.pdf" },
      JMM: { label: "Download Consent Form Template", url: "/forms/parent-consent-jmm.pdf" },
    };
    if (eventId === "SMM") {
      return isForeigner
        ? { label: "Download Consent Form - English", url: "/forms/parent-consent-smm-en.pdf" }
        : { label: "Unduh Formulir Persetujuan", url: "/forms/parent-consent-smm.pdf" };
    }
    return formMap[eventId] || {
      label: "Download Consent Form Template",
      url: "/forms/parent-consent.pdf",
    };
  };

  const countryNormalized = (formData.negara || "").toString().trim().toLowerCase();
  const isForeigner = Boolean(countryNormalized && countryNormalized !== "indonesia");
  const parentConsentForm = getParentConsentForm(eventId, isForeigner);

  // === SIZE CHART URL BASED ON EVENT ===
  const getSizeChartUrl = (eventId) => {
    const sizeChartMap = {
      SMM: "/jersey/sizechartsmm.jpg",
      JMM: "/jersey/jerseyjmm.png",
      BMM: "/jersey/jerseybmm.jpg",
    };
    return sizeChartMap[eventId] || "/jersey/jerseysmm.png";
  };

  const sizeChartUrl = getSizeChartUrl(eventId);

  // humanize with explicit mappings for Indonesian keys -> English labels
  const humanize = (str) => {
    if (!str) return "";
    const map = {
      nomorIdentitas: "Identification Number",
      performanceIndex: "ITRA ID",
      namaDepan: "First Name",
      namaBelakang: "Last Name",
      namaLengkap: "Full Name",
      telepon: "Phone/WhatsApp Number",
      tanggalLahir: "Date of Birth",
      gender: "Gender",
      negara: "Country",
      provinsi: "Province",
      kota: "City",
      alamat: "Address",
      kontakDarurat: "Emergency Contact Number",
      namaKontakDarurat: "Emergency Contact Name",
      hubunganKontakDarurat: "Emergency Contact Relationship",
      golonganDarah: "Blood Type",
      sizeJersey: "Jersey Size",
      bibName: "BIB Name",
      stravaProfile: "STRAVA Profile",
      teamName: "Team Name",
    };
    if (map[str]) return map[str];
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Load draft for single-user registration (if any)
  useEffect(() => {
    const fetchDraft = async () => {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      if (!userId || !eventId) {
        setIsDraftLoaded(true);
        return;
      }

      try {
        const docRef = doc(db, "registrations", `${userId}_${eventId}`);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Apply single-user draft fields (if present)
          const fields = [
            "namaDepan",
            "namaBelakang",
            "namaLengkap",
            "telepon",
            "nomorIdentitas",
            "tanggalLahir",
            "gender",
            "negara",
            "provinsi",
            "kota",
            "alamat",
            "kontakDarurat",
            "namaKontakDarurat",
            "hubunganKontakDarurat",
            "performanceIndex",
            "stravaProfile",
            "bibName",
            "golonganDarah",
            "sizeJersey",
          ];
          fields.forEach((f) => {
            if (data[f] !== undefined) {
              handleChange({ target: { name: f, value: data[f] } });
            }
          });
        }
      } catch (err) {
        // Silently handle permission errors - draft loading is optional
        // User can proceed with registration even if draft cannot be loaded
        console.log("[INFO] Draft registration not available (this is normal):", err.code);
      } finally {
        setIsDraftLoaded(true);
      }
    };

    fetchDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Initialize countries on mount using country-state-city package
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const countryList = allCountries.map((country) => ({
      value: country.name,
      label: country.name,
      isoCode: country.isoCode,
    }));
    setCountries(countryList);
    
    // Create a map for quick lookup
    const cMap = {};
    allCountries.forEach((country) => {
      cMap[country.name] = country.isoCode;
    });
    setCountryMap(cMap);
  }, []);

  // Load provinces when country changes
  useEffect(() => {
    if (formData.negara && countryMap[formData.negara]) {
      setProvinces([]);
      setCities([]);
      
      const countryCode = countryMap[formData.negara];
      const allStates = State.getStatesOfCountry(countryCode);
      
      const provinceList = allStates.map((state) => ({
        value: state.name,
        label: state.name,
        isoCode: state.isoCode,
      }));
      setProvinces(provinceList);
      
      // Create state map for quick lookup
      const sMap = {};
      allStates.forEach((state) => {
        sMap[state.name] = state.isoCode;
      });
      setStateMap(sMap);
    }
  }, [formData.negara, countryMap]);

  // Load cities when province changes
  useEffect(() => {
    if (formData.provinsi && formData.negara && countryMap[formData.negara] && stateMap[formData.provinsi]) {
      setCities([]);
      
      const countryCode = countryMap[formData.negara];
      const stateCode = stateMap[formData.provinsi];
      const allCities = City.getCitiesOfState(countryCode, stateCode);
      
      const cityList = allCities.map((city) => ({
        value: city.name,
        label: city.name,
      }));
      setCities(cityList);
    }
  }, [formData.provinsi, formData.negara, countryMap, stateMap]);


  const validateField = (name, value, negara = "Indonesia") => {
    const label = humanize(name);
    const valStr = value === undefined || value === null ? "" : String(value);

    // Optional fields: ITRA ID / STRAVA - validate only if provided
    if (
      (name === "performanceIndex" || name === "stravaProfile") &&
      (!valStr || valStr.trim() === "")
    ) {
      return "";
    }

    // Required check
    if (valStr.trim() === "") {
      return `${label} is required`;
    }

    // NAME (allow Unicode letters, spaces and common punctuation, max 50)
    if ((name === "namaDepan" || name === "namaBelakang") && valStr) {
      const trimmed = valStr.trim();
      if (trimmed.length > 50 || !/^[\p{L}\s'.-]{1,50}$/u.test(trimmed)) {
        return `${label} may only contain letters, spaces, or common name punctuation (max 50 characters).`;
      }
    }

    // BIB NAME (alphanumeric + spaces, 1-20)
    if (name === "bibName" && valStr) {
      const trimmed = valStr.trim();
      if (trimmed.length > 20 || !/^[A-Za-z0-9\s]+$/.test(trimmed)) {
        return `${label} must be 1-20 alphanumeric characters or spaces (e.g. "BUDI123").`;
      }
    }

    // PHONE (more tolerant): strip non-digits and validate length 6-15
    if ((name === "telepon" || name === "kontakDarurat") && valStr) {
      const digits = valStr.replace(/\D/g, "");
      if (digits.length < 6 || digits.length > 15) {
        return `${label} must contain 6-15 digits (you may include country code), e.g. "+6281234567890".`;
      }
    }

    // IDENTIFICATION: accept either 16 digits (national ID) OR 5-20 alphanumeric (passport)
    if (name === "nomorIdentitas" && valStr) {
      const trimmed = valStr.trim();
      if (!/^\d{16}$/.test(trimmed) && !/^[A-Za-z0-9]{5,20}$/.test(trimmed)) {
        return `${label} must be 16 digits or 5-20 alphanumeric characters (passport-style).`;
      }
    }

    // DATE OF BIRTH: category-based age validation
    // - 10K category: any age allowed (under-18 requires parent consent, handled separately)
    // - Non-10K categories: minimum 18 years required
    if (name === "tanggalLahir" && valStr) {
      const catLowerRace = (categoryName || "").toString().toLowerCase();
      const date = new Date(valStr);
      const today = new Date();
      
      if (isNaN(date.getTime()) || date >= today) {
        return `${label} must be a date in the past.`;
      }
      
      // Calculate age BASED ON EVENT DATE
      const refDate = eventDate || new Date();
      let age = refDate.getFullYear() - date.getFullYear();
      const monthDiff = refDate.getMonth() - date.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && refDate.getDate() < date.getDate())
      ) {
        age--;
      }

      // JMM Special Rule: Strictly 18+ for ALL categories
      if (eventId === "JMM") {
        if (age < 18) {
          return `Participant must be at least 18 years old to register for this event.`;
        }
      } else {
        // Kaldera Kids Run is a children's race: 17 is the ceiling. Keyed on
        // the name rather than is10KCategory, which also matches the open
        // BMM 10K and JMM 10K categories.
        if (catLowerRace.includes("kids") && age > 17) {
          return `Participant must be 17 years old or younger for this category.`;
        }

        // SMM 7K: minimum age is 13; under-18 participants require consent.
        if (isSMM7KCategory) {
          if (age < 13) {
            return `Participant must be at least 13 years old for this category.`;
          }
        } else if (!is10KCategory && age < 18) {
          return `Participant must be at least 18 years old for this category.`;
        }
      }

      // Basic sanity check for all categories
      if (age < 5) {
        return `Please verify the date of birth is correct.`;
      }
    }

    // BLOOD TYPE
    if (name === "golonganDarah" && valStr) {
      if (!["A", "B", "AB", "O"].includes(valStr)) {
        return `${label} must be one of A, B, AB, or O.`;
      }
    }

    // JERSEY SIZE
    if (name === "sizeJersey" && valStr) {
      if (!["XS", "S", "M", "L", "XL", "XXL"].includes(valStr)) {
        return `${label} must be XS, S, M, L, XL, or XXL.`;
      }
    }

    // GENDER (accept case-insensitive Male/Female)
    if (name === "gender" && valStr) {
      const g = valStr.toString().trim().toLowerCase();
      if (!["male", "female"].includes(g)) {
        return `${label} must be 'Male' or 'Female'.`;
      }
    }

    // COUNTRY/PROVINCE/CITY selection validations (if options loaded)
    if (name === "negara" && valStr) {
      if (countries.length > 0 && !countries.some((c) => c.value === valStr)) {
        return `${label} must be selected from the available list.`;
      }
    }
    if (name === "provinsi" && valStr) {
      if (provinces.length > 0 && !provinces.some((p) => p.value === valStr)) {
        return `${label} must be selected from the available list.`;
      }
    }
    if (name === "kota" && valStr) {
      if (cities.length > 0 && !cities.some((c) => c.value === valStr)) {
        return `${label} must be selected from the available list.`;
      }
    }

    // ADDRESS: relaxed validation, trim then check length 3-250, allow newline/tab, disallow problematic control chars
    if (name === "alamat" && valStr) {
      const trimmedAlamat = valStr.trim();
      if (trimmedAlamat.length < 3 || trimmedAlamat.length > 250) {
        return `${label} must be 3-250 characters, e.g. "Merdeka St No 123".`;
      }
      // Disallow control chars except newline (\n), carriage return (\r) and tab (\t)
      if (
        /[\x00\x01\x02\x03\x04\x05\x06\x07\x08\x0B\x0C\x0E-\x1F]/.test(valStr)
      ) {
        return `${label} contains invalid characters.`;
      }
    }

    // EMERGENCY CONTACT NAME / RELATIONSHIP (Unicode letters allowed)
    if (
      (name === "namaKontakDarurat" || name === "hubunganKontakDarurat") &&
      valStr
    ) {
      const trimmed = valStr.trim();
      if (trimmed.length > 50 || !/^[\p{L}\s'.-]{1,50}$/u.test(trimmed)) {
        return `${label} may only contain letters and spaces (max 50 characters).`;
      }
    }

    // ITRA ID validation (numeric 6-9 digits) if provided
    if (name === "performanceIndex" && valStr) {
      const trimmed = valStr.trim();
      if (!/^\d{6,9}$/.test(trimmed)) {
        return `${label} must be numeric (6-9 digits), e.g. "4515263". Leave empty if not available.`;
      }
    }

    return "";
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    // sanitize minimal: keep original value but trim when validating/storing if desired in updateUserData
    handleChange(e);
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value, formData.negara);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const getError = (field) => {
    const localError = errors[field];
    const formError = formData.errors?.[field];
    return touched[field] ? localError || formError : formError;
  };

  const openModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsModalOpen(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      <InputField
        label="First Name"
        name="namaDepan"
        value={formData.namaDepan || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("namaDepan")}
        labelColor={labelColor}
      />
      <InputField
        label="Last Name"
        name="namaBelakang"
        value={formData.namaBelakang || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("namaBelakang")}
        labelColor={labelColor}
      />
      <InputField
        label="BIB Name"
        name="bibName"
        value={formData.bibName || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("bibName")}
        labelColor={labelColor}
      />
      <InputField
        label="Identification Number"
        name="nomorIdentitas"
        value={formData.nomorIdentitas || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("nomorIdentitas")}
        labelColor={labelColor}
        placeholder={
          formData.negara === "Indonesia"
            ? "Enter 16-digit national ID (Indonesia)"
            : "Enter passport/ID number"
        }
      />
      <InputField
        label="Date of Birth"
        name="tanggalLahir"
        type="date"
        value={formData.tanggalLahir || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("tanggalLahir")}
        labelColor={labelColor}
      />
      <InputField
        label="Blood Type"
        name="golonganDarah"
        type="select"
        value={formData.golonganDarah || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        options={[
          { value: "", label: "Select Blood Type" },
          { value: "A", label: "A" },
          { value: "B", label: "B" },
          { value: "AB", label: "AB" },
          { value: "O", label: "O" },
        ]}
        error={getError("golonganDarah")}
        labelColor={labelColor}
      />

      {/* Parent consent spans a complete row after Date of Birth and Blood Type. */}
      {showParentConsent && (
        <div className="md:col-span-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className={`font-semibold ${labelColor || "text-slate-800"}`}>
                Parent/Guardian Consent Required
              </span>
            </div>
            <p className="text-sm text-amber-700">
              Participants under 18 years old must upload a signed Parent/Guardian Consent Form.
            </p>
            <a
              href={parentConsentForm.url}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 self-start text-sm text-cyan-600 hover:text-cyan-800 underline"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              {parentConsentForm.label}
            </a>
            <div>
              <InputField
                label="Upload Signed Consent Form"
                name="parentConsentFile"
                type="file"
                required
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  // Always mirror the picker into formData -- including a cleared
                  // selection -- so the confirmation below and handleSubmit read
                  // the exact same value. Keeping a second copy in local state let
                  // the green check show a file that formData no longer had.
                  if (file && file.size > 5 * 1024 * 1024) {
                    setParentConsentError("File size must be less than 5MB");
                    handleChange({
                      target: {
                        name: "parentConsentFile",
                        value: null,
                        type: "file",
                      },
                    });
                    return;
                  }
                  setParentConsentError("");
                  handleChange({
                    target: {
                      name: "parentConsentFile",
                      value: file,
                      type: "file",
                    },
                  });
                }}
                disabled={isFormDisabled || parentConsentUploading}
                error={parentConsentError}
                labelColor={labelColor}
              />
              {formData.parentConsentFile && (
                <p className="mt-1 text-sm text-emerald-600 flex items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {formData.parentConsentFile.name}
                </p>
              )}
              {parentConsentUploading && (
                <p className="mt-1 text-sm text-slate-600">Uploading...</p>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="relative">
        <InputField
          label="Jersey Size"
          name="sizeJersey"
          type="select"
          value={formData.sizeJersey || ""}
          onChange={handleFieldChange}
          disabled={isFormDisabled}
          required
          options={[
            { value: "", label: "Select Jersey Size" },
            { value: "XS", label: "XS" },
            { value: "S", label: "S" },
            { value: "M", label: "M" },
            { value: "L", label: "L" },
            { value: "XL", label: "XL" },
            { value: "XXL", label: "XXL" },
          ]}
          error={getError("sizeJersey")}
          labelColor={labelColor}
        />
        <button
          type="button"
          onClick={openModal}
          className="absolute top-0 right-0 mt-2 mr-2 text-cyan-600 hover:text-cyan-800 text-sm underline"
          disabled={isFormDisabled}
        >
          View Size Chart
        </button>
      </div>
      <InputField
        label="Gender"
        name="gender"
        type="select"
        value={formData.gender || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("gender")}
        labelColor={labelColor}
        options={[
          { value: "", label: "Select Gender" },
          { value: "Male", label: "Male" },
          { value: "Female", label: "Female" },
        ]}
      />
      <InputField
        label="Phone/WhatsApp Number"
        name="telepon"
        value={formData.telepon || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("telepon")}
        labelColor={labelColor}
      />
      <InputField
        label="Country"
        name="negara"
        type="select"
        value={formData.negara || "Indonesia"}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        options={[{ value: "", label: "Select Country" }, ...countries]}
        required
        error={getError("negara")}
        labelColor={labelColor}
      />
      <InputField
        label="Province"
        name="provinsi"
        type="select"
        value={formData.provinsi || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.negara}
        options={[{ value: "", label: "Select Province" }, ...provinces]}
        required
        error={getError("provinsi")}
        labelColor={labelColor}
      />
      <InputField
        label="City"
        name="kota"
        type="select"
        value={formData.kota || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.provinsi}
        options={[{ value: "", label: "Select City" }, ...cities]}
        required
        error={getError("kota")}
        labelColor={labelColor}
      />
      <InputField
        label="Address"
        name="alamat"
        value={formData.alamat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("alamat")}
        labelColor={labelColor}
        placeholder="e.g. Merdeka St No 123"
      />
      <InputField
        label="Emergency Contact Number"
        name="kontakDarurat"
        value={formData.kontakDarurat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("kontakDarurat")}
        labelColor={labelColor}
      />
      <InputField
        label="Emergency Contact Name"
        name="namaKontakDarurat"
        value={formData.namaKontakDarurat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("namaKontakDarurat")}
        labelColor={labelColor}
      />
      <InputField
        label="Emergency Contact Relationship"
        name="hubunganKontakDarurat"
        value={formData.hubunganKontakDarurat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={getError("hubunganKontakDarurat")}
        labelColor={labelColor}
      />

      {/* ITRA ID (formerly ITRA Performance Index) - optional */}
      <InputField
        label="ITRA ID"
        name="performanceIndex"
        value={formData.performanceIndex || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={getError("performanceIndex")}
        labelColor={labelColor}
        placeholder="e.g. 4515263 (Optional)"
      />

      {/* Strava Profile - OPTIONAL */}
      <InputField
        label="STRAVA Profile Link or similar"
        name="stravaProfile"
        value={formData.stravaProfile || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={getError("stravaProfile")}
        labelColor={labelColor}
        placeholder="e.g. https://www.strava.com/athletes/72528358 (Optional)"
      />

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={sizeChartUrl}
                alt="Size Chart"
                className="w-full h-auto"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InputFieldRace;
